import type { TimelineAudioConfigDict } from '../../../../generated/nodeDataTypes'

export type TimelineAudioKind = NonNullable<TimelineAudioConfigDict['audio_kind']>
export type LegacyTimelineAudioRole = NonNullable<TimelineAudioConfigDict['role']>
export type EffectiveTimelineAudioRole = LegacyTimelineAudioRole

export const TIMELINE_AUDIO_KIND_VALUES: TimelineAudioKind[] = [
  'audio',
  'music',
  'sfx',
  'speech',
  'extracted_original_audio',
  'changed_voice',
  'isolated_voice',
  'isolated_background',
]

const TIMELINE_AUDIO_KIND_SET = new Set<string>(TIMELINE_AUDIO_KIND_VALUES)

const AUDIO_KIND_TO_ROLE: Record<TimelineAudioKind, EffectiveTimelineAudioRole> = {
  audio: 'original',
  music: 'music',
  sfx: 'sfx',
  speech: 'replacement',
  extracted_original_audio: 'original',
  changed_voice: 'replacement',
  isolated_voice: 'replacement',
  isolated_background: 'sfx',
}

const LEGACY_ROLE_SET = new Set<string>(['original', 'replacement', 'sfx', 'music'])

const LEGACY_ROLE_TO_AUDIO_KIND: Record<LegacyTimelineAudioRole, TimelineAudioKind> = {
  original: 'audio',
  replacement: 'speech',
  sfx: 'sfx',
  music: 'music',
}

export function isTimelineAudioKind(value: string | null | undefined): value is TimelineAudioKind {
  return typeof value === 'string' && TIMELINE_AUDIO_KIND_SET.has(value)
}

export function isLegacyTimelineAudioRole(value: string | null | undefined): value is LegacyTimelineAudioRole {
  return typeof value === 'string' && LEGACY_ROLE_SET.has(value)
}

export function audioKindForLegacyRole(
  role: LegacyTimelineAudioRole | null | undefined,
): TimelineAudioKind | undefined {
  return role ? LEGACY_ROLE_TO_AUDIO_KIND[role] : undefined
}

export function getEffectiveAudioRole(
  audio: AudioKindCarrier,
): EffectiveTimelineAudioRole {
  const audioKind = 'audioKind' in audio
    ? audio.audioKind
    : 'audio_kind' in audio
      ? audio.audio_kind
      : undefined
  if (isTimelineAudioKind(audioKind)) {
    return AUDIO_KIND_TO_ROLE[audioKind]
  }
  if (isLegacyTimelineAudioRole(audio.role)) {
    return audio.role
  }
  return 'original'
}

export function isReplacementAudio(
  audio: AudioKindCarrier,
): boolean {
  return getEffectiveAudioRole(audio) === 'replacement'
}

export function isOriginalAudio(
  audio: AudioKindCarrier,
): boolean {
  return getEffectiveAudioRole(audio) === 'original'
}

export function isBackgroundAudio(
  audio: AudioKindCarrier,
): boolean {
  const role = getEffectiveAudioRole(audio)
  return role === 'sfx' || role === 'music'
}

export type TimelineAudioTrackLabelKey =
  | 'audioTrack'
  | 'originalAudioTrack'
  | 'voiceTrack'
  | 'musicTrack'
  | 'soundEffectsTrack'
  | 'backgroundAudioTrack'
  | 'mixedAudioTrack'

type TrackKindGroup = Exclude<TimelineAudioTrackLabelKey, 'audioTrack' | 'mixedAudioTrack'>

function audioKindTrackGroup(clip: FrontendAudioKindCarrier): TrackKindGroup | null {
  switch (clip.audioKind) {
    case 'extracted_original_audio':
      return 'originalAudioTrack'
    case 'speech':
    case 'changed_voice':
    case 'isolated_voice':
      return 'voiceTrack'
    case 'music':
      return 'musicTrack'
    case 'sfx':
      return 'soundEffectsTrack'
    case 'isolated_background':
      return 'backgroundAudioTrack'
    case 'audio':
      return null
    default:
      break
  }

  if (clip.role === 'replacement') return 'voiceTrack'
  if (clip.role === 'music') return 'musicTrack'
  if (clip.role === 'sfx') return 'soundEffectsTrack'
  return null
}

export function getAudioTrackLabelKey(clips: readonly FrontendAudioKindCarrier[]): TimelineAudioTrackLabelKey {
  const groups = new Set<TrackKindGroup>()
  for (const clip of clips) {
    const group = audioKindTrackGroup(clip)
    if (group) groups.add(group)
  }
  if (groups.size === 0) return 'audioTrack'
  if (groups.size > 1) return 'mixedAudioTrack'
  return Array.from(groups)[0]
}

export function fallbackAudioLabelForKind(audioKind: TimelineAudioKind | undefined): string {
  switch (audioKind) {
    case 'music':
      return 'Music'
    case 'sfx':
      return 'Sound effect'
    case 'speech':
      return 'Speech'
    case 'extracted_original_audio':
      return 'Original audio'
    case 'changed_voice':
      return 'Changed voice'
    case 'isolated_voice':
      return 'Isolated voice'
    case 'isolated_background':
      return 'Background audio'
    case 'audio':
    default:
      return 'Audio'
  }
}

export function audioLabelWithPartSuffix(label: string | undefined, index: number): string {
  const base = label?.trim() || 'Audio'
  return `${base} (${index})`
}
type FrontendAudioKindCarrier = {
  audioKind?: TimelineAudioKind
  role?: LegacyTimelineAudioRole | null
}

type PersistedAudioKindCarrier = {
  audio_kind?: TimelineAudioKind | null
  role?: LegacyTimelineAudioRole | null
}

type AudioKindCarrier = FrontendAudioKindCarrier | PersistedAudioKindCarrier
